import React from "react";

export type Boat = {
    id: string;
    category: string;
    title: string;
    heroSubtitle?: string;
    location?: string;
    images: string[];
    panel?: "left" | "right";
    features?: {
        icon: string;
        label: string;
    }[];
    featuresDetailed?: {
        label: string;
        value: string;
    }[];
    description?: { title: string; content: string };
    dealsSectionAnnotation?: string;
};

export type CategoryWithBoats = {
    id: number;
    category: string;
    title: string;
    subtitle: string;
    image: string;
    panel?: "left" | "right";
    link?: string;
    content?: {
        title: string;
        content: string;
    };
    boats?: Boat[];
    heroText?: string;
    price?: number;
};

export const yachtTypes: CategoryWithBoats[] = [
    {
        id: 1,
        category: "sailing",
        title: "Sailing Yachts",
        subtitle: "Forever Starts Here",
        image: "/assets/images/grid/Charter_4.jpg",
        panel: "left",
        link: "/yachts/sailing",
        content: {
            title: "Whether you're a seasoned sailor or setting off on your first escape under sail, a luxury sailing yacht offers a truly special way to explore the coast with family or friends. Combining the freedom of the open water with stylish comfort, these yachts provide an intimate, relaxed setting for a memorable experience at sea.",
            content: "From classic sailing yachts to contemporary catamarans, there are a wide range of beautifully maintained vessels available through trusted partners. Ideal for smaller groups, they offer everything from en-suite cabins and alfresco dining spaces to attentive crew and modern amenities — perfect for exploring hidden coves, peaceful anchorages, and sun-drenched island shores in complete comfort."
        },
        boats: [
            {
                id: "1",
                category: "sailing",
                title: "Wind Whisperer",
                images: ["/assets/images/grid/sailing_1.jpg"],
                panel: "left",
            },
            {
                id: "2",
                category: "sailing",
                title: "Sunset Sail",
                images: ["/assets/images/grid/sailing_2.jpg"],
                panel: "left",
            },
        ],
        heroText: "Sailing yachts with style, space, and serenity",
        price: 13_234,
    },
    {
        id: 2,
        category: "catamarans",
        title: "Catamarans",
        subtitle: "Freestyle your journey",
        image: "/assets/images/grid/Charter_3.jpg",
        panel: "right",
        link: "/yachts/catamarans",
        boats: [
            {
                id: "sunsail-424",
                category: "catamarans",
                title: "Sunsail 424",
                location: "Antigua & Barbuda",
                heroSubtitle: "4 Cabin Catamaran",
                dealsSectionAnnotation: "This yacht. Your next escape. View charter availability and pricing below",
                images: [
                    "/assets/images/yachts/yacht-1/1.avif",
                    "/assets/images/yachts/yacht-1/2.avif",
                    "/assets/images/yachts/yacht-1/3.avif",
                    "/assets/images/yachts/yacht-1/4.avif",
                    "/assets/images/yachts/yacht-1/5.avif",
                    "/assets/images/yachts/yacht-1/6.avif",
                ],
                features: [
                    {
                        icon: "fa-bed",
                        label: "6 Cabins"
                    },
                    {
                        icon: "fa-toilet",
                        label: "4 Toilets"
                    },
                    {
                        icon: "fa-ship",
                        label: "Catamaran"
                    },
                    {
                        icon: "fa-star",
                        label: "Premier"
                    },
                    {
                        icon: "fa-ruler",
                        label: "42ft"
                    },
                    {
                        icon: "fa-users",
                        label: "12 guests max."
                    },
                    {
                        icon: "fa-snowflake",
                        label: "Air Conditioning"
                    },
                    {
                        icon: "fa-bolt",
                        label: "Generator"
                    },
                    {
                        icon: "fa-tint",
                        label: "Watermaker - desalinator"
                    }
                ],
                featuresDetailed: [
                    {
                        label: "CABINS",
                        value: "6"
                    },
                    {
                        label: "TOILETS",
                        value: "4"
                    },
                    {
                        label: "GUESTS",
                        value: "12"
                    },
                    {
                        label: "LENGTH",
                        value: "42ft"
                    }

                ],
                description: {
                    title: "The newest catamaran from Robertson & Caine to join our fleet, the Sunsail 424 was designed with innovation and charter guests in mind. This mid-size 4-cabin sailing vessel is as functional as it is visually appealing. With sleek lines, intuitive handling, and spacious upper lounge area, the Sunsail 424 never fails to turn heads on the water.",
                    content: "Cruise in style and luxury while enjoying the scenic views through wrap-around windows.  The roomy interior features a skylight, U-shaped forward-facing galley, island berths for dual-sided access to the bed, and a forward door for ease of movement around the yacht. The Sunsail 424 also includes additional features such as underwater lights, a generator, fitted cushions and a water purifying system."
                }
            },
        ],
        heroText: "Explore in comfort with a modern catamaran yacht",
        content: {
            title: "Offering exceptional space, stability, and comfort, catamarans are a popular choice for relaxed sailing holidays with family or friends. With wide decks, open-plan living areas, and panoramic views from almost every angle, they provide a light and airy way to enjoy life on the water — perfect for groups looking to unwind in style.",
            content: "We work with trusted partners to offer access to a selection of modern, well-appointed catamarans in stunning destinations. Whether island-hopping through turquoise lagoons or dropping anchor in a peaceful bay, these yachts combine easy handling with generous comfort — ideal for those who value freedom, privacy, and a touch of luxury at sea."
        },
        price: 3_234,
    },
    {
        id: 3,
        subtitle: "Barefoot Elegance Awaits",
        title: "Motor Yachts",
        image: "/assets/images/grid/Charter_5.jpg",
        panel: "right",
        link: "/yachts/motor",
        category: "motor",
        content: {
            title: "Crewed motor yachts are a popular option for those seeking a luxurious charter experience, offering spacious accommodation, elegant surroundings, and a full complement of water toys and onboard facilities.With attentive crew to look after your every need, these yachts deliver an exceptional standard of service, making them ideal for a truly indulgent escape at sea.",
            content: "Whether you're looking to soak up the atmosphere at major international events like the Monaco Grand Prix or prefer the tranquillity of a remote anchorage halfway around the world, a luxury motor yacht offers the perfect setting. From the glamorous ports of the French Riviera to the exotic waters of Tahiti, you'll enjoy the freedom to explore in complete comfort with family and friends."
        },
        heroText: "Motor yacht luxury, wherever the sea takes you",
        price: 5_234,
    },
    {
        id: 4,
        subtitle: "Adventure, Unscripted",
        title: "Open Yachts",
        image: "/assets/images/grid/Charter_1.jpg",
        panel: "right",
        link: "/yachts/open",
        category: "open",
        content: {
            title: "For those who value style, speed, and a sociable atmosphere on the water, open yachts offer an exhilarating way to travel in comfort. With sleek designs, powerful engines, and spacious outdoor areas ideal for sunbathing or entertaining, these yachts are built for coastal cruising with a modern, high-end edge.",
            content: "Through our trusted partners, we offer access to a range of open yachts ideal for day trips or short stays in glamorous coastal locations. Whether you’re chasing the sun along the Côte d’Azur or enjoying a long lunch at anchor in Ibiza, these yachts are perfect for those who want to combine performance with luxury and enjoy the journey as much as the destination."
        },
        heroText: "Experience sleek, sociable cruising on an open yacht",
        price: 4_234,
    },
    {
        id: 5,
        subtitle: "Family Time, Redefined",
        title: "Gulet Yachts",
        image: "/assets/images/grid/Gulet.jpg",
        panel: "left",
        category: "gulet",
        link: "/yachts/gulet",
        content: {
            title: "Blending traditional charm with modern comfort, gulet yachts offer a unique and relaxing way to explore the coast with a small group of friends or family. With their wide decks, spacious cabins, and characterful wooden interiors, these classic sailing vessels bring a slower, more authentic pace to life on the water — all complemented by warm, attentive service on board.",
            content: "Working with trusted partners, we offer access to a handpicked selection of high-quality gulets, ideal for discovering quiet bays, fishing villages, and scenic island routes. Many come equipped with air-conditioned cabins, alfresco dining areas, and even water toys for added enjoyment — perfect for those seeking comfort, character, and the freedom to explore in style."
        },
        heroText: "Relaxed coastal cruising on a classic gulet yacht",
        price: 2_234,
    },
    {
        id: 6,
        subtitle: "Everything, Beautifully Covered",
        title: "Classic Yachts",
        image: "/assets/images/grid/classic.jpg",
        panel: "left",
        link: "/yachts/classic",
        category: "classic",
        heroText: "Savour timeless elegance aboard a classic yacht",
        content: {
            title: "For those drawn to timeless elegance, classic yachts offer a beautifully refined way to experience life at sea. With graceful lines, polished woodwork, and a sense of nautical heritage, these vessels blend vintage charm with discreet comfort — ideal for couples or small groups looking to slow down and savour the journey.",
            content: "Through our trusted partners, we can arrange access to a curated range of classic yachts, each lovingly maintained and rich in character. Whether sailing through Mediterranean waters or anchoring in a secluded cove, these yachts offer a more romantic and evocative way to explore — where every voyage feels like a step back into the golden age of travel."
        },
        price: 1_234,
    },
];
